import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pin, MapPin, Languages, Sparkles, ExternalLink } from "lucide-react";
import { PageHeader, Badge } from "@/components/ui/PageHeader";
import {
  listAnnouncements,
  getAnnouncementReactions,
  setAnnouncementReaction,
  clearAnnouncementReaction,
  GARV_REACTIONS,
  TYPE_LABELS,
  ANNOUNCEMENT_TYPES,
  type AnnouncementCard,
  type GarvReactionsView,
} from "@/api/announcements";

/**
 * The announcements strip, with its targeting made visible.
 *
 * The rule this page exists to demonstrate is the binding one: cultural
 * attributes BOOST ranking and never hide content. An announcement with
 * affinityState=Maharashtra appears for everyone — people from MH just see it
 * higher. The single hard filter is `requiresLanguage`, and it is legitimate
 * only when the thing itself cannot be joined without the language. So every
 * chip on a card here answers "why is this ranked where it is", and the one
 * thing you can NEVER observe on this page is a card hidden because of where
 * someone's family is from. If that ever becomes observable, the rule broke.
 *
 * The place controls impersonate geography (the server honours explicit
 * city/state over your profile). Language cannot be impersonated — the
 * barrier always reads your DECLARED languages, so what you see is the honest
 * answer for the account you are signed in as.
 */
export default function AnnouncementsLab() {
  const [type, setType] = useState<string>("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  // Only refetch on explicit apply — typing "San" must not query for "San".
  const [applied, setApplied] = useState<{ city?: string; state?: string }>({});

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["announcements", type, applied],
    queryFn: () =>
      listAnnouncements({
        type: type || undefined,
        city: applied.city,
        state: applied.state,
        limit: 20,
      }),
  });

  const list = data?.data;

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Announcements"
        subtitle="What's happening around you — with the targeting shown, not hidden."
        breadcrumbs={[{ label: "Lab", href: "/" }, { label: "Announcements" }]}
      />

      {/* ── the rule, stated where the evidence is ─────────────── */}
      <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-600">
        <p>
          <Sparkles className="mr-1 inline h-3.5 w-3.5 text-amber-500" />
          <strong className="text-gray-900">Affinity boosts, never hides.</strong>{" "}
          A card tagged for a state still reaches everyone — origin moves it up
          the list for some people, and that is all origin is allowed to do.
          The only hard filter is a declared-language barrier, and news can
          never carry one.
        </p>
      </div>

      {/* ── controls ────────────────────────────────────────────── */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <label className="text-xs text-gray-500">
          Type
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="mt-1 block rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
          >
            <option value="">All</option>
            {ANNOUNCEMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t] ?? t}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-gray-500">
          See it from a city
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="e.g. Edison"
            className="mt-1 block w-36 rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>
        <label className="text-xs text-gray-500">
          State
          <input
            value={state}
            onChange={(e) => setState(e.target.value.toUpperCase())}
            placeholder="NJ"
            maxLength={2}
            className="mt-1 block w-16 rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>
        <button
          onClick={() =>
            setApplied({ city: city.trim() || undefined, state: state.trim() || undefined })
          }
          className="rounded-full bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700"
        >
          Apply
        </button>
        {(applied.city || applied.state) && (
          <button
            onClick={() => {
              setApplied({});
              setCity("");
              setState("");
            }}
            className="text-sm text-gray-500 hover:text-gray-900"
          >
            Back to my own location
          </button>
        )}
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {isError && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error instanceof Error ? error.message : "Failed to load"}
        </div>
      )}

      {list && list.announcements.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center">
          <p className="font-medium text-gray-900">Nothing here right now</p>
          <p className="mt-1 text-sm text-gray-500">
            No active announcements match this place and type.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {list?.announcements.map((a) => <Card key={a.id} a={a} />)}
      </div>

      {list && (
        <p className="mt-5 text-xs text-gray-400">
          {list.meta.total} active · pinned first, then nearest scope, then your
          affinity, then newest — the server sends this order and the Lab does
          not re-sort it.
        </p>
      )}
    </div>
  );
}

function Card({ a }: { a: AnnouncementCard }) {
  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="blue">{TYPE_LABELS[a.type] ?? a.type}</Badge>
        <Badge variant="gray">{a.scope}</Badge>
        {a.isPinned && (
          <Badge variant="purple">
            <Pin className="mr-1 h-3 w-3" /> pinned
          </Badge>
        )}
        {/* Affinity is shown, not hidden — the point is that you can see this
            card even when the state is not yours. */}
        {a.affinityState && (
          <Badge variant="yellow">
            <Sparkles className="mr-1 h-3 w-3" />
            boosted for {a.affinityState} — visible to all
          </Badge>
        )}
        {/* If you can read this card, you clear its barrier by definition. */}
        {a.requiresLanguage && (
          <Badge variant="green">
            <Languages className="mr-1 h-3 w-3" />
            {a.requiresLanguage} speakers — you declared it
          </Badge>
        )}
      </div>

      <h2 className="mt-2 font-medium text-gray-900">{a.title}</h2>
      <p className="mt-1 text-sm text-gray-600">{a.summary}</p>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        {(a.city || a.state) && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {[a.city, a.state].filter(Boolean).join(", ")}
          </span>
        )}
        {a.source && <span>via {a.source}</span>}
        {a.provider && <span>· about {a.provider.name}</span>}
        <span>· {new Date(a.publishedAt).toLocaleDateString()}</span>
        {a.linkUrl && (
          <a
            href={a.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1 font-medium text-blue-700 hover:underline"
          >
            {a.linkLabel ?? "Read more"} <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      <GarvChips announcementId={a.id} />
    </article>
  );
}

/**
 * The Garv chips. One reaction per person, changeable — pressing your current
 * chip takes it back. What renders underneath is COMPOSITION ("3 people from
 * 2 states are proud of this"), never a counter: the server sends no total,
 * so there is nothing to race.
 */
const REACTION_PHRASES: Record<string, string> = {
  proud: "proud of this",
  congratulating: "congratulating this",
  inspired: "inspired by this",
  excited: "excited about this",
  grateful: "grateful for this",
};

function GarvChips({ announcementId }: { announcementId: string }) {
  const [view, setView] = useState<GarvReactionsView | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    getAnnouncementReactions(announcementId)
      .then((res) => alive && setView(res.data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [announcementId]);

  const react = async (reaction: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const res =
        view?.myReaction === reaction
          ? await clearAnnouncementReaction(announcementId)
          : await setAnnouncementReaction(announcementId, reaction);
      setView(res.data);
    } finally {
      setBusy(false);
    }
  };

  const top = view?.composition[0];

  return (
    <div className="mt-3 border-t border-gray-100 pt-2">
      <div className="flex flex-wrap gap-1.5">
        {GARV_REACTIONS.map((r) => (
          <button
            key={r}
            onClick={() => void react(r)}
            disabled={busy || !view}
            className={
              view?.myReaction === r
                ? "rounded-full border border-amber-400 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800"
                : "rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            }
          >
            {r}
          </button>
        ))}
      </div>
      {top && (
        <p className="mt-1.5 text-[11px] text-gray-500">
          {top.people} {top.people === 1 ? "person" : "people"}
          {top.states > 0 &&
            ` from ${top.states} ${top.states === 1 ? "state" : "states"}`}{" "}
          {top.people === 1 ? "is" : "are"}{" "}
          {REACTION_PHRASES[top.reaction] ?? `${top.reaction} about this`}
        </p>
      )}
    </div>
  );
}
